import type { StudentAccount, StudentWorkspace } from '@/entities/student-workspace'

export interface WorkspaceDirectory {
  revision: number
  studentIndex: StudentAccount[]
  workspaces: Record<string, StudentWorkspace>
  /** Opaque Zustand payload retained by the compatibility Facade. */
  serializedState?: string | null
  /** Durable idempotency receipts. Snapshots in this map never contain the map itself. */
  mutationReceipts?: Record<string, { revision: number; snapshot: Omit<WorkspaceDirectory, 'mutationReceipts'> }>
}

export type MutableWorkspaceDirectory = Omit<WorkspaceDirectory, 'revision'>
export type WorkspaceMutation = (draft: MutableWorkspaceDirectory) => void

export type WorkspaceCommitResult =
  | { status: 'committed'; mutationId: string; revision: number; snapshot: WorkspaceDirectory }
  | { status: 'duplicate'; mutationId: string; revision: number; snapshot: WorkspaceDirectory }
  | { status: 'conflict'; mutationId: string; expectedRevision: number; revision: number; snapshot: WorkspaceDirectory }
  | { status: 'readonly'; mutationId: string; revision: number; snapshot: WorkspaceDirectory }

export type WorkspaceListener = (snapshot: WorkspaceDirectory) => void

export interface WorkspaceRepository {
  load(): Promise<WorkspaceDirectory>
  transact(expectedRevision: number, mutationId: string, mutation: WorkspaceMutation): Promise<WorkspaceCommitResult>
  subscribe(listener: WorkspaceListener): () => void
  exportRecovery(): Promise<WorkspaceDirectory>
}

export class WorkspaceRevisionConflictError extends Error {
  constructor(public readonly result: Extract<WorkspaceCommitResult, { status: 'conflict' }>) {
    super(`Workspace revision conflict: expected ${result.expectedRevision}, found ${result.revision}`)
    this.name = 'WorkspaceRevisionConflictError'
  }
}

export function emptyWorkspaceDirectory(): WorkspaceDirectory {
  return { revision: 0, studentIndex: [], workspaces: {}, serializedState: null }
}

export function cloneWorkspaceDirectory(value: WorkspaceDirectory): WorkspaceDirectory {
  if (typeof structuredClone === 'function') return structuredClone(value)
  return JSON.parse(JSON.stringify(value)) as WorkspaceDirectory
}

export function assertMutationId(mutationId: string): void {
  if (!mutationId.trim()) throw new TypeError('Workspace mutationId must not be empty')
}
